import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Paper,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { Link as RouterLink, useLocation } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadarIcon from "@mui/icons-material/Radar";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import ArticleIcon from "@mui/icons-material/Article";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedIcon from "@mui/icons-material/Verified";
import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_LIGHT = "#DDFBF8";
const TURQUOISE_PALE = "#F3FFFD";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_LIGHT = "#FFF3CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_LIGHT = "#FBEAF0";

const WHITE = "#FFFFFF";
const NAVY = "#102A43";
const SLATE = "#5A6473";

const BODY_FONT =
  "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const LOGO_SRC = "/logo.png";
const FAVICON_SRC = "/favicon.png";

const iconFloat = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-3px) rotate(-3deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const softShine = keyframes`
  0% { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
  30% { opacity: 0.75; }
  100% { transform: translateX(170%) skewX(-18deg); opacity: 0; }
`;

const logoFloat = keyframes`
  0%, 100% { transform: translate(-50%, -50%) translateY(0); }
  50% { transform: translate(-50%, -50%) translateY(-2px); }
`;

const mainActions = [
  {
    label: "Find Art",
    subtitle: "Decorate my room",
    to: "/artist-room-matcher",
    icon: <TravelExploreIcon sx={{ fontSize: 22 }} />,
    variant: "find",
  },
  {
    label: "Upload Art",
    subtitle: "Paintings & sculptures",
    to: "/artist-launch",
    icon: <CloudUploadIcon sx={{ fontSize: 22 }} />,
    variant: "upload",
  },
];

const extraItems = [
  {
    label: "Painting Analyzer",
    subtitle: "Title, palette, technique and price helper",
    to: "/artist-painting-analyzer",
    icon: <AutoAwesomeIcon />,
  },
  {
    label: "Opportunities",
    subtitle: "Funding, grants and useful creative paths",
    to: "/creative-tech-opportunities",
    icon: <RadarIcon />,
  },
  {
    label: "Art Radar",
    subtitle: "Daily art and local culture signals",
    to: "/StartYourDay",
    icon: <WbSunnyIcon />,
  },
  {
    label: "Creator Radar",
    subtitle: "Daily useful article for artists",
    to: "/creator-radar",
    icon: <ArticleIcon />,
  },
  {
    label: "Work With Us",
    subtitle: "Student, artist and creative missions",
    to: "/student-artist-mission",
    icon: <GroupsIcon />,
  },
];

const commitmentItems = [
  {
    label: "Privacy Policy",
    subtitle: "How we handle buyer, artist and image information",
    to: "/commitments/privacy-policy",
    icon: <VerifiedIcon />,
  },
  {
    label: "Social Impact",
    subtitle: "How we support artists, buyers and creative learning",
    to: "/commitments/social-impact",
    icon: <GroupsIcon />,
  },
  {
    label: "Environmental Sustainability",
    subtitle: "Thoughtful art discovery, less waste and longer-life objects",
    to: "/commitments/environmental-sustainability",
    icon: <PaletteRoundedIcon />,
  },
];

export default function SiteNavbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [extrasAnchorEl, setExtrasAnchorEl] = useState(null);
  const location = useLocation();

  const extrasOpen = Boolean(extrasAnchorEl);

  useEffect(() => {
    let favicon = document.querySelector('link[rel="icon"]');

    if (!favicon) {
      favicon = document.createElement("link");
      favicon.setAttribute("rel", "icon");
      document.head.appendChild(favicon);
    }

    favicon.setAttribute("href", FAVICON_SRC);
    favicon.setAttribute("type", "image/png");

    let shortcutIcon = document.querySelector('link[rel="shortcut icon"]');

    if (!shortcutIcon) {
      shortcutIcon = document.createElement("link");
      shortcutIcon.setAttribute("rel", "shortcut icon");
      document.head.appendChild(shortcutIcon);
    }

    shortcutIcon.setAttribute("href", FAVICON_SRC);
    shortcutIcon.setAttribute("type", "image/png");
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const openExtras = (event) => {
    setExtrasAnchorEl(event.currentTarget);
  };

  const closeExtras = () => {
    setExtrasAnchorEl(null);
  };

  const isActive = (item) => {
    if (!item?.to) return false;

    if (item.to === "/") {
      return location.pathname === "/";
    }

    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  };

  const extrasActive =
    extraItems.some(isActive) ||
    commitmentItems.some(isActive) ||
    location.pathname.startsWith("/commitments");

  const actionStyles = (variant, active = false) => {
    if (variant === "upload") {
      return {
        background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 44%, ${BURGUNDY} 100%)`,
        shadow: active
          ? "0 18px 48px rgba(212,175,55,0.32)"
          : "0 12px 30px rgba(122,18,50,0.18)",
        iconColor: GOLD_DARK,
        accent: GOLD,
      };
    }

    return {
      background: `linear-gradient(135deg, ${TURQUOISE} 0%, ${TURQUOISE_DARK} 56%, ${BURGUNDY_DARK} 100%)`,
      shadow: active
        ? "0 18px 48px rgba(0,180,166,0.34)"
        : "0 12px 30px rgba(16,42,67,0.18)",
      iconColor: TURQUOISE_DARK,
      accent: TURQUOISE,
    };
  };

  const renderDesktopAction = (item) => {
    const active = isActive(item);
    const styles = actionStyles(item.variant, active);

    return (
      <Button
        key={item.label}
        component="a"
        href={item.to}
        aria-label={`${item.label}: ${item.subtitle}`}
        sx={{
          position: "relative",
          overflow: "hidden",
          color: WHITE,
          textTransform: "none",
          fontFamily: BODY_FONT,
          borderRadius: "23px",
          px: 1.3,
          py: 1.05,
          minWidth: 184,
          background: styles.background,
          boxShadow: styles.shadow,
          border: active
            ? `1px solid ${alpha(styles.accent, 0.76)}`
            : "1px solid rgba(255,255,255,0.18)",
          transition: "transform 0.18s ease, box-shadow 0.18s ease",
          "&:hover": {
            background: styles.background,
            transform: "translateY(-3px)",
            boxShadow: "0 18px 42px rgba(16,42,67,0.22)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            width: "42%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.34), transparent)",
            animation: `${softShine} 4.8s ease-in-out infinite`,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.1}
          alignItems="center"
          sx={{
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "16px",
              display: "grid",
              placeItems: "center",
              color: styles.iconColor,
              background: "rgba(255,255,255,0.92)",
              flexShrink: 0,
              animation: `${iconFloat} 3.2s ease-in-out infinite`,
            }}
          >
            {item.icon}
          </Box>

          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                display: "block",
                fontWeight: 950,
                fontSize: "0.98rem",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Typography>

            <Typography
              component="span"
              sx={{
                display: "block",
                color: "rgba(255,255,255,0.78)",
                fontWeight: 800,
                fontSize: "0.7rem",
                lineHeight: 1.1,
                mt: 0.3,
                whiteSpace: "nowrap",
              }}
            >
              {item.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Button>
    );
  };

  const renderExtraMenuItem = (item) => {
    const active = isActive(item);

    return (
      <MenuItem
        key={item.label}
        component={RouterLink}
        to={item.to}
        onClick={closeExtras}
        sx={{
          borderRadius: "20px",
          p: 1.35,
          mx: 1,
          my: 0.55,
          minWidth: 330,
          backgroundColor: active ? alpha(TURQUOISE, 0.1) : "transparent",
          border: active
            ? `1px solid ${alpha(TURQUOISE, 0.24)}`
            : "1px solid transparent",
          "&:hover": {
            backgroundColor: alpha(TURQUOISE, 0.08),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: 1.4 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "15px",
              display: "grid",
              placeItems: "center",
              color: active ? WHITE : TURQUOISE_DARK,
              background: active
                ? `linear-gradient(135deg, ${TURQUOISE}, ${GOLD}, ${BURGUNDY})`
                : TURQUOISE_LIGHT,
              "& svg": {
                fontSize: 21,
              },
            }}
          >
            {item.icon}
          </Box>
        </ListItemIcon>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: active ? TURQUOISE_DARK : NAVY,
              fontWeight: 950,
              lineHeight: 1.12,
              fontSize: "0.98rem",
            }}
          >
            {item.label}
          </Typography>

          <Typography
            sx={{
              color: SLATE,
              fontWeight: 650,
              lineHeight: 1.35,
              fontSize: "0.78rem",
              mt: 0.3,
              whiteSpace: "normal",
            }}
          >
            {item.subtitle}
          </Typography>
        </Box>
      </MenuItem>
    );
  };

  const renderMobileMainAction = (item) => {
    const active = isActive(item);
    const styles = actionStyles(item.variant, active);

    return (
      <Button
        key={item.label}
        component="a"
        href={item.to}
        onClick={closeDrawer}
        fullWidth
        sx={{
          justifyContent: "flex-start",
          borderRadius: "24px",
          px: 1.35,
          py: 1.25,
          textTransform: "none",
          color: WHITE,
          background: styles.background,
          boxShadow: styles.shadow,
          overflow: "hidden",
          position: "relative",
          "&:hover": {
            background: styles.background,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            width: "42%",
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)",
            animation: `${softShine} 5s ease-in-out infinite`,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.2}
          alignItems="center"
          sx={{
            width: "100%",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "18px",
              display: "grid",
              placeItems: "center",
              color: styles.iconColor,
              backgroundColor: "rgba(255,255,255,0.92)",
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Box>

          <Box sx={{ textAlign: "left", minWidth: 0, flexGrow: 1 }}>
            <Typography
              sx={{
                fontFamily: BODY_FONT,
                fontWeight: 950,
                fontSize: "1.05rem",
                lineHeight: 1.1,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 800,
                fontSize: "0.8rem",
                mt: 0.3,
              }}
            >
              {item.subtitle}
            </Typography>
          </Box>

          <ArrowForwardIcon sx={{ color: "rgba(255,255,255,0.88)" }} />
        </Stack>
      </Button>
    );
  };

  const renderMobileExtraItem = (item) => {
    const active = isActive(item);

    return (
      <ListItemButton
        key={item.label}
        onClick={closeDrawer}
        component={RouterLink}
        to={item.to}
        sx={{
          borderRadius: "19px",
          mb: 0.9,
          px: 1.4,
          py: 1.15,
          border: active
            ? `1px solid ${alpha(TURQUOISE, 0.42)}`
            : `1px solid ${alpha(TURQUOISE, 0.09)}`,
          backgroundColor: active ? alpha(TURQUOISE, 0.11) : "rgba(255,255,255,0.74)",
          "&:hover": {
            backgroundColor: alpha(TURQUOISE, 0.1),
          },
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "15px",
            display: "grid",
            placeItems: "center",
            mr: 1.35,
            color: active ? WHITE : TURQUOISE_DARK,
            background: active
              ? `linear-gradient(135deg, ${TURQUOISE}, ${GOLD}, ${BURGUNDY})`
              : TURQUOISE_LIGHT,
            flexShrink: 0,
            "& svg": {
              fontSize: 21,
            },
          }}
        >
          {item.icon}
        </Box>

        <ListItemText
          primary={item.label}
          secondary={item.subtitle}
          primaryTypographyProps={{
            fontFamily: BODY_FONT,
            fontWeight: active ? 950 : 850,
            color: active ? TURQUOISE_DARK : NAVY,
            fontSize: "0.99rem",
            lineHeight: 1.2,
          }}
          secondaryTypographyProps={{
            fontFamily: BODY_FONT,
            color: SLATE,
            fontWeight: 600,
            fontSize: "0.78rem",
            lineHeight: 1.35,
            mt: 0.3,
          }}
        />
      </ListItemButton>
    );
  };

  const renderMenuSectionHeader = ({ icon, title, subtitle, color = TURQUOISE_DARK }) => (
    <Box sx={{ px: 1.3, pt: 1, pb: 0.6 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "14px",
            display: "grid",
            placeItems: "center",
            color,
            background:
              color === BURGUNDY ? BURGUNDY_LIGHT : color === GOLD_DARK ? GOLD_LIGHT : TURQUOISE_LIGHT,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography sx={{ color: NAVY, fontWeight: 950, lineHeight: 1.1 }}>
            {title}
          </Typography>
          <Typography sx={{ color: SLATE, fontWeight: 650, fontSize: "0.78rem" }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          zIndex: 1200,
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(24px)",
          color: NAVY,
          borderBottom: `1px solid ${alpha(TURQUOISE, 0.12)}`,
          boxShadow: "0 10px 36px rgba(16,42,67,0.06)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              position: "relative",
              minHeight: { xs: "76px !important", md: "98px !important" },
              px: { xs: 1, md: 1.5 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                gap: 1,
                flex: 1,
                justifyContent: "flex-start",
                zIndex: 3,
              }}
            >
              {mainActions.map(renderDesktopAction)}
            </Box>

            <Box
              component={RouterLink}
              to="/"
              aria-label="Art For My Room home"
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                textDecoration: "none",
                width: { xs: 74, sm: 84, md: 98, lg: 108 },
                height: { xs: 54, sm: 62, md: 72, lg: 78 },
                display: "grid",
                placeItems: "center",
                zIndex: 2,
                animation: { xs: "none", lg: `${logoFloat} 4.8s ease-in-out infinite` },
                pointerEvents: "auto",
              }}
            >
              <Box
                component="img"
                src={LOGO_SRC}
                alt="Art For My Room logo"
                loading="eager"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  filter: `
                    drop-shadow(0 10px 16px rgba(0,180,166,0.20))
                    drop-shadow(0 6px 12px rgba(212,175,55,0.14))
                  `,
                }}
              />
            </Box>

            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                justifyContent: "flex-end",
                flex: 1,
                zIndex: 3,
              }}
            >
              <Button
                onClick={openExtras}
                endIcon={
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      transform: extrasOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                }
                startIcon={<AppsRoundedIcon />}
                sx={{
                  px: 1.7,
                  py: 1.08,
                  borderRadius: "22px",
                  textTransform: "none",
                  fontWeight: 920,
                  fontFamily: BODY_FONT,
                  color: extrasActive ? BURGUNDY : NAVY,
                  backgroundColor: extrasActive
                    ? alpha(BURGUNDY, 0.09)
                    : alpha(TURQUOISE_PALE, 0.92),
                  border: extrasActive
                    ? `1px solid ${alpha(BURGUNDY, 0.26)}`
                    : `1px solid ${alpha(TURQUOISE, 0.13)}`,
                  boxShadow: extrasActive
                    ? "0 12px 28px rgba(122,18,50,0.10)"
                    : "none",
                  "&:hover": {
                    backgroundColor: alpha(TURQUOISE, 0.1),
                  },
                  "& .MuiButton-startIcon svg": {
                    fontSize: 21,
                    color: extrasActive ? BURGUNDY : TURQUOISE_DARK,
                  },
                }}
              >
                More
              </Button>
            </Box>

            <Box
              sx={{
                display: { xs: "block", lg: "none" },
                width: 48,
                height: 48,
                zIndex: 3,
              }}
            />

            <IconButton
              aria-label="Open navigation menu"
              onClick={() => setDrawerOpen(true)}
              sx={{
                display: { xs: "inline-flex", lg: "none" },
                position: "relative",
                zIndex: 20,
                color: BURGUNDY,
                backgroundColor: alpha(BURGUNDY, 0.08),
                border: `1px solid ${alpha(BURGUNDY, 0.16)}`,
                ml: "auto",
                "&:hover": {
                  backgroundColor: alpha(BURGUNDY, 0.13),
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Menu
        anchorEl={extrasAnchorEl}
        open={extrasOpen}
        onClose={closeExtras}
        keepMounted
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.2,
            p: 1,
            borderRadius: "28px",
            maxHeight: "calc(100vh - 130px)",
            overflowY: "auto",
            background: `
              radial-gradient(circle at top left, ${alpha(TURQUOISE, 0.12)}, transparent 34%),
              radial-gradient(circle at bottom right, ${alpha(GOLD, 0.14)}, transparent 32%),
              radial-gradient(circle at 88% 16%, ${alpha(BURGUNDY, 0.08)}, transparent 34%),
              rgba(255,255,255,0.97)
            `,
            backdropFilter: "blur(24px)",
            border: `1px solid ${alpha(TURQUOISE, 0.16)}`,
            boxShadow: "0 28px 80px rgba(16,42,67,0.16)",
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        {renderMenuSectionHeader({
          icon: <SchoolIcon sx={{ fontSize: 20 }} />,
          title: "Extra services",
          subtitle: "Helpful tools around the platform",
          color: BURGUNDY,
        })}

        <Divider sx={{ my: 1, borderColor: alpha(TURQUOISE, 0.13) }} />

        {extraItems.map(renderExtraMenuItem)}

        <Divider sx={{ my: 1.2, borderColor: alpha(BURGUNDY, 0.14) }} />

        {renderMenuSectionHeader({
          icon: <VerifiedIcon sx={{ fontSize: 20 }} />,
          title: "Commitments",
          subtitle: "Privacy, impact and sustainability",
          color: TURQUOISE_DARK,
        })}

        <Divider sx={{ my: 1, borderColor: alpha(TURQUOISE, 0.13) }} />

        {commitmentItems.map(renderExtraMenuItem)}
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: "92vw", sm: 430 },
            maxWidth: 450,
            background: `
              radial-gradient(circle at top left, ${alpha(TURQUOISE, 0.16)}, transparent 36%),
              radial-gradient(circle at bottom right, ${alpha(GOLD, 0.16)}, transparent 30%),
              radial-gradient(circle at 90% 16%, ${alpha(BURGUNDY, 0.10)}, transparent 32%),
              linear-gradient(180deg, #ffffff 0%, #f4fffe 100%)
            `,
            borderLeft: `1px solid ${alpha(TURQUOISE, 0.18)}`,
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2.25 }}
          >
            <Box
              component={RouterLink}
              to="/"
              onClick={closeDrawer}
              sx={{
                width: 90,
                height: 64,
                display: "grid",
                placeItems: "center",
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={LOGO_SRC}
                alt="Art For My Room logo"
                loading="lazy"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  filter: "drop-shadow(0 8px 12px rgba(0,180,166,0.18))",
                }}
              />
            </Box>

            <IconButton
              aria-label="Close navigation menu"
              onClick={closeDrawer}
              sx={{
                color: BURGUNDY,
                backgroundColor: alpha(BURGUNDY, 0.08),
                border: `1px solid ${alpha(BURGUNDY, 0.16)}`,
                "&:hover": {
                  backgroundColor: alpha(BURGUNDY, 0.13),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "24px",
              backgroundColor: WHITE,
              border: `1px solid ${alpha(TURQUOISE, 0.15)}`,
              boxShadow: "0 12px 28px rgba(16,42,67,0.06)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <VerifiedIcon sx={{ color: TURQUOISE_DARK, fontSize: 22 }} />
              <Typography sx={{ color: NAVY, fontWeight: 950, fontSize: "1rem" }}>
                Choose your path
              </Typography>
            </Stack>

            <Typography sx={{ color: SLATE, lineHeight: 1.62, fontSize: "0.94rem" }}>
              Decorate your room with matched art, or upload original work so it can find the right space.
            </Typography>
          </Paper>

          <Stack spacing={1.15} sx={{ mb: 2.25 }}>
            {mainActions.map(renderMobileMainAction)}
          </Stack>

          <Divider sx={{ borderColor: alpha(TURQUOISE, 0.16), mb: 2 }} />

          <Chip
            icon={<AppsRoundedIcon />}
            label="Extra services"
            sx={{
              mb: 1.4,
              fontWeight: 900,
              color: BURGUNDY,
              backgroundColor: BURGUNDY_LIGHT,
              "& .MuiChip-icon": {
                color: BURGUNDY,
              },
            }}
          />

          <List sx={{ p: 0 }}>
            {extraItems.map(renderMobileExtraItem)}
          </List>

          <Divider sx={{ borderColor: alpha(TURQUOISE, 0.16), my: 2 }} />

          <Chip
            icon={<VerifiedIcon />}
            label="Commitments"
            sx={{
              mb: 1.4,
              fontWeight: 900,
              color: TURQUOISE_DARK,
              backgroundColor: TURQUOISE_LIGHT,
              "& .MuiChip-icon": {
                color: TURQUOISE_DARK,
              },
            }}
          />

          <List sx={{ p: 0 }}>
            {commitmentItems.map(renderMobileExtraItem)}
          </List>

          <Divider sx={{ borderColor: alpha(TURQUOISE, 0.16), my: 2 }} />

          <List sx={{ p: 0 }}>
            <ListItemButton
              onClick={closeDrawer}
              component={RouterLink}
              to="/"
              sx={{
                borderRadius: "19px",
                px: 1.4,
                py: 1.15,
                backgroundColor: "rgba(255,255,255,0.72)",
                border: `1px solid ${alpha(TURQUOISE, 0.09)}`,
                "&:hover": {
                  backgroundColor: alpha(TURQUOISE, 0.1),
                },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "15px",
                  display: "grid",
                  placeItems: "center",
                  mr: 1.35,
                  color: TURQUOISE_DARK,
                  background: TURQUOISE_LIGHT,
                  flexShrink: 0,
                }}
              >
                <HomeRoundedIcon sx={{ fontSize: 21 }} />
              </Box>

              <ListItemText
                primary="Home"
                secondary="Back to the main page"
                primaryTypographyProps={{
                  fontFamily: BODY_FONT,
                  fontWeight: 850,
                  color: NAVY,
                  fontSize: "0.99rem",
                  lineHeight: 1.2,
                }}
                secondaryTypographyProps={{
                  fontFamily: BODY_FONT,
                  color: SLATE,
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  lineHeight: 1.35,
                  mt: 0.3,
                }}
              />
            </ListItemButton>
          </List>

          <Box
            sx={{
              mt: 2.6,
              p: 2,
              borderRadius: "24px",
              background: `linear-gradient(135deg, ${alpha(TURQUOISE, 0.09)} 0%, ${alpha(GOLD, 0.15)} 100%)`,
              border: `1px solid ${alpha(GOLD, 0.24)}`,
              overflow: "hidden",
            }}
          >
            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 0.8 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "14px",
                  display: "grid",
                  placeItems: "center",
                  background: WHITE,
                  color: TURQUOISE_DARK,
                  boxShadow: "0 8px 18px rgba(16,42,67,0.08)",
                }}
              >
                <PaletteRoundedIcon sx={{ fontSize: 20 }} />
              </Box>

              <Typography sx={{ color: NAVY, fontWeight: 950 }}>
                Turquoise, gold and burgundy
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: SLATE,
                fontSize: "0.92rem",
                lineHeight: 1.62,
              }}
            >
              The platform focuses on simple actions: find art for my room, upload original art, and use helpful creative tools.
            </Typography>

            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.4 }}>
              <Chip
                size="small"
                icon={<LocalOfferRoundedIcon />}
                label="10% commission"
                sx={{
                  backgroundColor: GOLD_LIGHT,
                  color: GOLD_DARK,
                  fontWeight: 850,
                  "& .MuiChip-icon": {
                    color: GOLD_DARK,
                  },
                }}
              />

              <Chip
                size="small"
                icon={<AutoAwesomeIcon />}
                label="AI matching"
                sx={{
                  backgroundColor: TURQUOISE_LIGHT,
                  color: TURQUOISE_DARK,
                  fontWeight: 850,
                  "& .MuiChip-icon": {
                    color: TURQUOISE_DARK,
                  },
                }}
              />
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
